import { useEffect, useMemo, useState } from "react";
import EventHelper from "../../../common/network/helpers/event/EventHelper";
import { EventEditionDetails } from "../../../common/types";
import { getStoredColocatedShows, saveStoredColocatedShows } from "../../../common/utils/colocatedShowsUtils";

const sortById = (items: EventEditionDetails[]) => [...items].sort((a, b) => (a.id || "").localeCompare(b.id || ""));

const areSameSelection = (a: EventEditionDetails[], b: EventEditionDetails[]) => {
  if (a.length !== b.length) return false;
  const aSorted = sortById(a);
  const bSorted = sortById(b);
  return aSorted.every((item, idx) => item.id === bSorted[idx]?.id);
};

export const useColocatedShows = (selectedEventId: string) => {
  const [colocatedShows, setColocatedShows] = useState<EventEditionDetails[]>([]);
  const [selectedShows, setSelectedShows] = useState<EventEditionDetails[]>([]);
  const [storedShows, setStoredShows] = useState<EventEditionDetails[]>([]);
  const [loading, setLoading] = useState(false);

  const loadStoredShows = async (available: EventEditionDetails[]) => {
    const stored = await getStoredColocatedShows();
    const filtered = stored.filter(show => available.some(a => a.id === show.id));
    setStoredShows(filtered);
    setSelectedShows(filtered);
  };

  useEffect(() => {
    const fetchColocatedShows = async () => {
      if (!selectedEventId) {
        setColocatedShows([]);
        setSelectedShows([]);
        setStoredShows([]);
        return;
      }

      setLoading(true);
      try {
        const eventEditionInfo = await EventHelper.fetchEventEdition(selectedEventId);
        const colocatedIds = await EventHelper.getColocatedEventEditionIds(eventEditionInfo.colocatedShows || []);

        if (!colocatedIds.length) {
          setColocatedShows([]);
          setSelectedShows([]);
          setStoredShows([]);
          setLoading(false);
          return;
        }

        const colocatedInfos = await Promise.all(colocatedIds.map(id => EventHelper.fetchEventEdition(id)));
        setColocatedShows(colocatedInfos);
        await loadStoredShows(colocatedInfos);
      } catch (e) {
        console.warn("[colocated] fetch colocated shows error", e);
        setColocatedShows([]);
        setSelectedShows([]);
        setStoredShows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchColocatedShows();
  }, [selectedEventId]);

  const shouldColocatedUpdate = useMemo(() => !areSameSelection(selectedShows, storedShows), [selectedShows, storedShows]);

  const onToggleShow = (show: EventEditionDetails) => {
    setSelectedShows(prev => {
      const exists = prev.some(item => item.id === show.id);
      if (exists) {
        return prev.filter(item => item.id !== show.id);
      }
      return [...prev, show];
    });
  };

  const saveColocatedShows = async () => {
    await saveStoredColocatedShows(selectedShows);
    setStoredShows(selectedShows);
  };

  const resetColocatedShows = () => {
    setSelectedShows(storedShows);
  };

  return {
    colocatedShows,
    selectedShows,
    onToggleShow,
    shouldColocatedUpdate,
    saveColocatedShows,
    resetColocatedShows,
    loading
  };
};
